// TandMList2.jsx
export default function TandMList2({
  activeTab,
  setActiveTab,
  filteredTimeAndMaterials,
  handleCreateToggle,
  handleEdit,
  handleView,
  handleStatusChange,
  handleConvertToChangeOrder,
  costCodeOptions,
  availableLabourItems,
  vendorOptions,
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white tracking-tight">
          Time and Material Entries
        </h2>
        <button
          onClick={handleCreateToggle}
          className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200 shadow-sm"
        >
          New Entry
        </button>
      </div>

      <div className="border-b border-gray-600 mb-4">
        <nav className="flex space-x-4">
          {["Pending", "Approved", "Rejected"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-4 text-sm font-medium rounded-t-md transition-colors duration-200 ${
                activeTab === tab
                  ? "bg-gray-600 text-orange-400 border-b-2 border-orange-400"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                T&M #
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Created By
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Scope of Work
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Approved By
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Budget $
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                CO Link
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider actions-column">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {filteredTimeAndMaterials.length > 0 ? (
              filteredTimeAndMaterials.map((entry) => {
                const items = entry.items || [];
                const entries = entry.entries || [];

                // Original items budget total
                const originalBudgetTotal = items.reduce(
                  (sum, item) =>
                    sum +
                    (item.forBudget !== false
                      ? Math.abs(item.amount || item.totalCost || 0)
                      : 0),
                  0,
                );

                // Approved entry items budget total
                const entriesBudgetTotal = entries
                  .filter((e) => e.status === "Approved")
                  .flatMap((e) => e.items || [])
                  .reduce(
                    (sum, item) =>
                      sum +
                      (item.forBudget !== false
                        ? Math.abs(item.amount || item.totalCost || 0)
                        : 0),
                    0,
                  );

                const combinedBudgetTotal =
                  originalBudgetTotal + entriesBudgetTotal;

                const itemCount = items.length;
                const materialCount = items.filter(
                  (i) => i.category === "M",
                ).length;
                const labourCount = items.filter(
                  (i) => i.category === "L",
                ).length;

                const totalEntries = entries.length;
                const pendingEntries = entries.filter(
                  (e) => e.status === "Pending",
                ).length;

                // CO link info
                const linkedCO = entry.linkedChangeOrderId;
                const linkedCONumber = linkedCO?.changeOrderNumber || null;
                const linkedCOStatus = linkedCO?.approvalStatus || null;

                return (
                  <tr
                    key={entry._id}
                    onClick={(e) => {
                      if (e.target.closest(".actions-column")) return;
                      handleView(entry);
                    }}
                    className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                  >
                    {/* T&M Number */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 font-medium">
                      #{entry.timeAndMaterialNumber || "—"}
                    </td>

                    {/* Created By */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {entry.createdBy?.displayName || "Unknown"}
                    </td>

                    {/* Scope of Work */}
                    <td className="px-6 py-4 text-sm text-gray-200 max-w-xs truncate">
                      {entry.scopeOfWork || "N/A"}
                    </td>

                    {/* Approved By */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      {entry.signedBy || "N/A"}
                    </td>

                    {/* Items + Entries badges */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {labourCount > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-blue-600 text-white">
                            {labourCount} L
                          </span>
                        )}
                        {materialCount > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-green-600 text-white">
                            {materialCount} M
                          </span>
                        )}
                        {itemCount === 0 && totalEntries === 0 && (
                          <span className="text-gray-400">—</span>
                        )}
                        {/* Additional entries badge */}
                        {totalEntries > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-orange-600/40 text-orange-300 border border-orange-500/30">
                            +{totalEntries}{" "}
                            {totalEntries === 1 ? "entry" : "entries"}
                          </span>
                        )}
                        {/* Pending entries badge */}
                        {pendingEntries > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            {pendingEntries} pending
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Budget $ — combined original + approved entries */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col">
                        <span className="text-blue-400">
                          ${combinedBudgetTotal.toFixed(2)}
                        </span>
                        {entriesBudgetTotal > 0 && (
                          <span className="text-[10px] text-gray-500 mt-0.5">
                            incl. ${entriesBudgetTotal.toFixed(2)} entries
                          </span>
                        )}
                      </div>
                    </td>

                    {/* CO Link */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {linkedCO ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-purple-400 font-medium text-xs">
                            CO #{linkedCONumber || "—"}
                          </span>
                          <span
                            className={`text-[10px] font-semibold ${
                              linkedCOStatus === "Approved"
                                ? "text-green-400"
                                : linkedCOStatus === "Rejected"
                                  ? "text-red-400"
                                  : "text-yellow-400"
                            }`}
                          >
                            {linkedCOStatus || "Pending"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.status === "Pending"
                            ? "bg-yellow-400/20 text-yellow-400"
                            : entry.status === "Approved"
                              ? "bg-green-400/20 text-green-400"
                              : "bg-red-400/20 text-red-400"
                        }`}
                      >
                        {entry.status}
                      </span>
                    </td>

                    {/* Actions — View and Edit only; approve/reject handled in entries panel */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-2 actions-column">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(entry);
                        }}
                        className="text-orange-400 hover:text-orange-300 transition-colors duration-200 font-medium"
                      >
                        View
                      </button>
                      {(entry.status === "Pending" ||
                        entry.status === "Rejected") && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(entry);
                          }}
                          className="text-blue-400 hover:text-blue-300 transition-colors duration-200 font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="9"
                  className="px-6 py-4 text-center text-sm text-gray-400"
                >
                  No entries available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
